import { useState, useEffect } from 'react'
import MainLayout from './components/Layout/MainLayout'
import UserProfile from './components/Dashboard/UserProfile'
import ProfitSection from './components/Dashboard/ProfitSection'
import QuickActions from './components/Dashboard/QuickActions'
import TodayEarned from './components/Dashboard/TodayEarned'
import ExchangesPage from './components/Exchanges/ExchangesPage'
import DiaryPage from './components/Diary/DiaryPage'
import BotsPage from './components/Bots/BotsPage'
import DashboardSkeleton from './components/common/Skeleton'
import ErrorBoundary from './components/common/ErrorBoundary'
import MaintenanceScreen from './components/common/MaintenanceScreen'
import WebLanding from './components/common/WebLanding'
import BootError from './components/common/BootError'
import SettingsModal from './components/modals/SettingsModal'
import InstructionModal from './components/modals/InstructionModal'
import ApiKeysGuideModal from './components/modals/ApiKeysGuideModal'
import SubscriptionModal from './components/modals/SubscriptionModal'
import SupportModal from './components/modals/SupportModal'
import LegalModal from './components/modals/LegalModal'
import AdminPanel from './components/Admin/AdminPanel'
import WelcomeHint from './components/Onboarding/WelcomeHint'
import HintsTour from './components/Onboarding/HintsTour'
import OnboardingFlow from './components/Onboarding/OnboardingFlow'
import TrialStatusCard from './components/Onboarding/TrialStatusCard'
import ModeChangeNotice from './components/UI/ModeChangeNotice'
import FortuneGate from './components/Fortune/FortuneGate'
import { useDashboardStore } from './store/dashboardStore'
import { useTelegram } from './hooks/useTelegram'
import { getMe, getConfig, getAppConfig, hasApiKeys, setImpersonate, adminSetViewMode, setModeNoticeSeen, bindReferral, sendClientLog } from './services/api'
import { captureUtm } from './services/utm'
import { shouldShowModeNotice } from './utils/modeNotice'
import { trialBotPending } from './utils/fortuneGate'

function DashboardPage() {
  return (
    <>
      <WelcomeHint />
      {/* П.0.3 (task.txt): карточка состояния пробного бота на Дашборде — «Экран 3»
          онбординга (CTA «Запустить», статус «ждёт выплату фандинга», плашка оплаты). */}
      <TrialStatusCard />
      <QuickActions />
      <TodayEarned />
      <UserProfile />
      <ProfitSection />
    </>
  )
}

// Реферальная привязка по start_param (п.4.4 ПЛАН_МАРКЕТИНГА). ВТОРОЙ путь:
// основной — бот принимает метку из ссылки t.me/<бот>?start=ref_XXXXXX и
// привязывает друга сам (TG-BOT/src/startPayload.js), ещё до открытия
// приложения. Здесь остаётся случай, когда главное мини-приложение включено и
// Telegram всё-таки отдал start_param. Шлём bindReferral fire-and-forget:
// бэкенд сам отклонит «уже платил» / «уже привязан» / «сам себя» /
// несуществующий код — это норма, не ошибка приложения (повторная привязка
// безвредна). Вызывается ПОСЛЕ getMe (юзер уже в БД).
function bindReferralFromStartParam() {
  try {
    const sp = window.Telegram?.WebApp?.initDataUnsafe?.start_param
    if (typeof sp !== 'string' || sp.indexOf('ref_') !== 0) return
    bindReferral(sp.slice(4)).catch(() => {})
  } catch { /* приватный режим и т.п. — не критично */ }
}

function PageContent({ activeTab }) {
  switch (activeTab) {
    case 'dashboard': return <DashboardPage />
    case 'portfolio': return <ExchangesPage />
    case 'bots':      return <BotsPage />
    case 'trading':   return <DiaryPage />
    case 'admin':     return <AdminPanel />
    default:          return <DashboardPage />
  }
}

function AppContent() {
  const { tg } = useTelegram()
  const activeTab        = useDashboardStore(s => s.activeTab)
  const activeModal      = useDashboardStore(s => s.activeModal)
  const setModal         = useDashboardStore(s => s.setActiveModal)
  const setBotsConfig    = useDashboardStore(s => s.setBotsConfig)
  const setSubscriptionEnd = useDashboardStore(s => s.setSubscriptionEnd)
  const setSubscriptionExpired = useDashboardStore(s => s.setSubscriptionExpired)
  const setHasApiKeys    = useDashboardStore(s => s.setHasApiKeys)
  const setAccessFlags   = useDashboardStore(s => s.setAccessFlags)
  const setPerTypeKeys   = useDashboardStore(s => s.setPerTypeKeys)
  const setVirtualOrders = useDashboardStore(s => s.setVirtualOrders)
  const setLanguage      = useDashboardStore(s => s.setLanguage)
  const setNotifications = useDashboardStore(s => s.setNotifications)
  const isAdmin          = useDashboardStore(s => s.isAdmin)
  const appConfig        = useDashboardStore(s => s.appConfig)
  const setIsAdmin       = useDashboardStore(s => s.setIsAdmin)
  const setAppConfig     = useDashboardStore(s => s.setAppConfig)
  const hintsEnabled     = useDashboardStore(s => s.hintsEnabled)
  const hintsDone        = useDashboardStore(s => s.hintsDone)
  const setHintsIntroSeen = useDashboardStore(s => s.setHintsIntroSeen)
  const closeModal       = () => setModal(null)

  // П.0.3 (task.txt): признаки «нового юзера» для онбординг-флоу. botsLoaded —
  // сигнал, что список ботов реально пришёл с сервера (иначе пустой userBots
  // на первом кадре ложно «свежий»). profileOk — getMe успешно отработал.
  const userBots         = useDashboardStore(s => s.userBots)
  const botsLoaded       = useDashboardStore(s => s.botsLoaded)
  const demoAllowed      = useDashboardStore(s => s.demoAllowed)
  const realAllowed      = useDashboardStore(s => s.realAllowed)
  const fetchUserBots    = useDashboardStore(s => s.fetchUserBots)

  const [loading, setLoading] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [profileOk, setProfileOk] = useState(false)
  // П.3 task.txt: критичный сбой загрузки (getMe не отдал профиль — нет сети или
  // отклонена авторизация). Раньше ошибка молча глоталась (.catch(() => {})) и
  // юзер получал пустое приложение без объяснения — это и выглядело как
  // «мини-апп не открывается». Теперь показываем понятный экран с «Повторить»
  // и пишем причину в диагностический лог.
  const [bootError, setBootError] = useState(null)

  // task.txt №3: одноразовое уведомление о смене рекомендуемого режима.
  // seenModeNoticeAt — метка юзера с бэкенда (users.mode_notice_seen_at);
  // modeNoticeDismissedFor — локальная защита от повторного показа в текущей
  // сессии, пока POST-подтверждение «юзер увидел» ещё летит на сервер.
  const [seenModeNoticeAt, setSeenModeNoticeAt] = useState(0)
  const [modeNoticeDismissedFor, setModeNoticeDismissedFor] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 900)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    // Режим юзера (п.1-7 task.txt): если он был включён в админке и страница
    // перезагрузилась, восстанавливаем флаг из localStorage — иначе первый же
    // запрос ушёл бы без X-Impersonate и бэкенд вернул бы админа, а не юзера.
    // Вызывается ДО getMe — заголовок должен попасть в первый запрос.
    // П.5 task.txt: при каждом входе в режим (тумблер ИЛИ перезагрузка) тестовый
    // юзер сбрасывается КАК АДМИН (путь /api/admin/ не имперсонируется). Иначе
    // после перезагрузки в БД остался бы виртуальный бот прошлой сессии, и кнопка
    // «Добавить бота» отвечала бы 409 «You already have a virtual trial bot».
    try {
      if (localStorage.getItem('scalpex-view-mode') === '1') {
        adminSetViewMode(true).catch(() => {})
        setImpersonate(true)
        // П.0.3: в режиме юзера онбординг первого входа должен показываться
        // КАЖДЫЙ раз (тестовый юзер сбрасывается на бэкенде). Сбрасываем и
        // локальный флаг «нажал Позже» — иначе он спрятал бы флоу от админа.
        localStorage.removeItem('scalpex-onboarding-skipped')
      }
    } catch { /* приватный режим */ }

    // UTM-метки трафика (стартовый параметр / адресная строка) фиксируются на бэкенде
    // при первом входе с метками. Fire-and-forget — не влияет на загрузку.
    captureUtm()

    getMe().then(profile => {
      if (profile?.subscription_end) setSubscriptionEnd(profile.subscription_end)
      // Подписка была, но истекла — блокируем ползунки ботов и показываем баннер.
      setSubscriptionExpired(profile?.subscription_expired ?? false)
      // Доступность типов счёта (демо/реальный) по подписке.
      setAccessFlags(profile)
      // Пробный виртуальный бот: счётчик использованных сделок — для плашки
      // оплаты после 2 закрытых виртуальных сделок.
      setVirtualOrders({ used: profile?.virtual_orders_used, limit: profile?.virtual_orders_limit })
      // Админ ли пользователь (по id в ADMIN_IDS/ADMIN_ID) — показываем вкладку «Админ».
      if (profile) setIsAdmin(profile.is_admin)
      // Язык, сохранённый ранее (настройки / /start), применяется постоянно —
      // даже на новом устройстве, пока пользователь не сменит его сам.
      if (profile?.language) setLanguage(profile.language === 'ru' ? 'ru' : 'en')
      // Тумблер «Торговые сигналы»: источник истины — БД (бот шлёт уведомления
      // о закрытых сделках по нему). Перезаписываем localStorage значением с
      // сервера — чтобы на новом устройстве тумблер был честным.
      if (typeof profile?.notif_trades === 'boolean') {
        const cur = useDashboardStore.getState().notifications
        setNotifications({ ...cur, trades: profile.notif_trades })
      }
      // Тумблер «Уведомления о фандингах» (п.8): источник истины — БД.
      if (typeof profile?.notif_funding === 'boolean') {
        const cur = useDashboardStore.getState().notifications
        setNotifications({ ...cur, funding: profile.notif_funding })
      }
      // task.txt №3: метка «юзер видел уведомление о смене режима» — фронт
      // сравнивает её с appConfig.modeChangedAt (показывать ли окно).
      if (profile) setSeenModeNoticeAt(Number(profile.modeNoticeSeenAt) || 0)
      // П.0.3: профиль успешно получен — можно принимать решение об онбординге
      // (без profileOk флоу не показываем, чтобы не «накрыть» подписчика при
      // сетевой ошибке getMe).
      setProfileOk(true)
      // Рефералка (п.4.4): привязываем открывшего по ссылке друга. Админу не
      // надо — он тестирует, а не «приходит по ссылке» (и тестовые id внутренние).
      if (!profile?.is_admin) bindReferralFromStartParam()
    }).catch(err => {
      // Критичный сбой: без профиля приложение не знает ни подписки, ни прав —
      // работать в нём нельзя. Показываем экран сбоя вместо пустого интерфейса.
      setProfileOk(false)
      setBootError(err instanceof Error ? err : new Error(String(err)))
      try {
        sendClientLog({
          stage: 'getMe',
          message: String(err?.message || err),
          platform: window.Telegram?.WebApp?.platform || 'unknown',
          version: window.Telegram?.WebApp?.version || '',
          hasInitData: !!window.Telegram?.WebApp?.initData,
          ua: navigator.userAgent,
        })
      } catch { /* диагностика не критична */ }
    })

    // Публичный конфиг приложения: feature-флаги, биржи, оплаты, «Тех. работы».
    // Если админ включил maintenance — покажем экран «Тех. работы».
    getAppConfig().then(cfg => {
      if (cfg) setAppConfig(cfg)
    }).catch(() => {})

    hasApiKeys().then(data => {
      setHasApiKeys(data?.hasKeys ?? false)
      setPerTypeKeys(data)
    }).catch(() => {
      setHasApiKeys(false)
    })

    // П.0.3: тянем ботов при старте приложения (не только на вкладке «Боты»),
    // чтобы Дашборд знал, есть ли у юзера пробный бот (TrialStatusCard) и
    // показывать ли онбординг первого входа. Дублирующийся запрос на вкладке
    // «Боты» безвреден (идемпотентен).
    fetchUserBots()

    const syncConfig = () => {
      getConfig().then(cfg => {
        if (cfg) setBotsConfig({
          minFundingRate:   cfg.minFundingRate,
          blacklistedPairs: cfg.blacklistedPairs ?? [],
          maxPositions:     cfg.maxPositions ?? 3,
        })
      }).catch(() => {})
    }

    syncConfig()

    // task.txt №3: на каждом возврате в приложение перечитываем appConfig и
    // метку юзера — если админ сменил режим, пока приложение было свёрнуто,
    // окно «рекомендуемый режим изменился» должно появиться при фокусе.
    const refreshModeNotice = () => {
      getAppConfig().then(cfg => { if (cfg) setAppConfig(cfg) }).catch(() => {})
      getMe().then(profile => {
        if (profile) setSeenModeNoticeAt(Number(profile.modeNoticeSeenAt) || 0)
      }).catch(() => {})
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        syncConfig()
        hasApiKeys().then(data => {
          setHasApiKeys(data?.hasKeys ?? false)
          setPerTypeKeys(data)
        }).catch(() => {})
        refreshModeNotice()
      }
    }

    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [setBotsConfig, setSubscriptionEnd, setSubscriptionExpired, setHasApiKeys, setAccessFlags, setPerTypeKeys, setLanguage, setNotifications, setAppConfig, setIsAdmin, setVirtualOrders, setSeenModeNoticeAt, fetchUserBots])

  // П.0.3 (task.txt): онбординг-флоу первого входа. Показываем НОВОМУ юзеру:
  // профиль загружен, скелет ушёл, боты реально пришли с сервера, юзер не админ,
  // подписка не даёт типов счёта, ботов нет, юзер не нажал «Позже». В режиме
  // юзера тестовый юзер сбрасывается (ботов нет) → флоу виден при каждом входе.
  useEffect(() => {
    if (!profileOk || loading || !botsLoaded || isAdmin) return
    if (demoAllowed || realAllowed || (Array.isArray(userBots) && userBots.length > 0)) return
    if (appConfig.maintenance) return
    let skipped = false
    try { skipped = localStorage.getItem('scalpex-onboarding-skipped') === '1' } catch { skipped = false }
    if (!skipped) setShowOnboarding(true)
  }, [profileOk, loading, botsLoaded, isAdmin, demoAllowed, realAllowed, userBots, appConfig.maintenance])

  // Онбординг пройден (бот создан и запущен) — welcome-карточка больше не нужна.
  const handleOnboardingDone = () => {
    setShowOnboarding(false)
    setHintsIntroSeen(true)
  }
  // «Позже»: прячем флоу до следующего входа (пока юзер не создал бота).
  const handleOnboardingSkip = () => {
    try { localStorage.setItem('scalpex-onboarding-skipped', '1') } catch { /* приватный режим */ }
    setShowOnboarding(false)
  }

  // Задача владельца 2026-10-21: новичку окно колеса фортуны не показываем —
  // оно не должно пересекаться с приглашением создать пробного бота (главное,
  // чтобы юзер создал бота). Правило вынесено в utils/fortuneGate.js, чтобы его
  // можно было проверить таблицей истинности: ждёт пробного бота = профиль и
  // боты пришли, подписки нет, ботов нет совсем либо есть только пробный
  // виртуальный бот. Админа правило не касается — он проверяет колесо сам.
  const isTrialPending = trialBotPending({
    profileOk, botsLoaded, isAdmin, demoAllowed, realAllowed, userBots,
  })

  // task.txt №3: показываем окно «рекомендуемый режим изменился», только если
  // админ менял режим ПОСЛЕ последней просмотренной юзером метки
  // (modeChangedAt > seenModeNoticeAt) и окно ещё не закрыто в этой сессии.
  // Батч E (п.1): условие дополнено подпиской — новичку с пробным ботом
  // рекомендуемый режим не показываем (забирает внимание). Метка «увидел» не
  // ставится, поэтому после оформления подписки окно появится само.
  const modeChangedAt = Number(appConfig?.modeChangedAt) || 0
  const showModeNotice = shouldShowModeNotice({
    profileOk,
    loading,
    isAdmin,
    modeChangedAt,
    seenModeNoticeAt,
    dismissedFor: modeNoticeDismissedFor,
    hasSubscription: !!(demoAllowed || realAllowed),
  })

  const handleDismissModeNotice = () => {
    const at = modeChangedAt || Date.now()
    setModeNoticeDismissedFor(at)   // не показывать снова в этой сессии
    setSeenModeNoticeAt(at)         // синхронно обновить локально
    setModeNoticeSeen(at).catch(() => {})  // запомнить на сервере (до след. смены)
  }

  // П.5 task.txt: вне Telegram (веб-ссылка) приложение не может авторизоваться —
  // нет initData. Вместо сломанного экрана показываем лендинг с кнопкой
  // «Открыть в Telegram»; UTM из адресной строки уходит в метку кнопки.
  if (!window.Telegram?.WebApp?.initData) {
    return <WebLanding />
  }

  // П.3 task.txt: профиль не загрузился (нет сети / авторизация отклонена) —
  // понятный экран с кнопкой «Повторить» вместо пустого приложения.
  if (bootError) {
    return (
      <BootError
        titleKey="bootLoadFailed"
        hintKey="bootLoadFailedHint"
        error={bootError}
        onRetry={() => { try { window.location.reload() } catch { /* ignore */ } }}
      />
    )
  }

  // Режим «Тех. работы»: всем пользователям показываем экран обслуживания
  // (админ видит приложение — может выключить режим во вкладке «Админ»).
  if (appConfig.maintenance && !isAdmin) {
    return <MaintenanceScreen />
  }

  return (
    <>
      <MainLayout>
        {loading ? <DashboardSkeleton /> : <PageContent activeTab={activeTab} />}
      </MainLayout>

      {activeModal === 'settings' && <SettingsModal onClose={closeModal} />}
      {activeModal === 'instruction' && <InstructionModal onClose={closeModal} />}
      {activeModal === 'api-keys' && <ApiKeysGuideModal onClose={closeModal} />}
      {activeModal === 'subscription' && <SubscriptionModal onClose={closeModal} />}
      {activeModal === 'support' && <SupportModal onClose={closeModal} />}
      {activeModal === 'legal' && <LegalModal onClose={closeModal} />}

      {/* Онбординг-флоу первого входа (п.0.3): поверх всего, пока не создан
          пробный бот / не нажато «Позже». Рендерится только при fresh-юзере. */}
      {showOnboarding && (
        <OnboardingFlow onDone={handleOnboardingDone} onSkip={handleOnboardingSkip} />
      )}

      {/* Онбординг-тур: только когда подсказки включены и ещё не пройдены.
          Админу не показываем — он и так знает приложение. */}
      {hintsEnabled && !hintsDone && !isAdmin && <HintsTour />}

      {/* task.txt №3: одноразовое уведомление по центру экрана о смене
          рекомендуемого режима. Админу не показываем — он и есть тот, кто
          меняет режим. Закрывается кнопкой/тапом; метка «увидел» уходит на
          сервер и держится до следующей смены режима. */}
      {showModeNotice && (
        <ModeChangeNotice
          mode={appConfig?.recommendedTradeMode === 'long' ? 'long' : 'short'}
          onDismiss={handleDismissModeNotice}
        />
      )}

      {/* Розыгрыш бонусов (колесо фортуны): маленькое окно при входе.
          Не мешаем онбордингу первого входа и туру подсказок — новичку
          сначала нужно создать бота. Флаги включения приходят в appConfig.
          Новичку с пробным ботом окно тоже молчит (trialBotPending): день
          помечается просмотренным, кнопка колеса у края остаётся. */}
      {!loading && !showOnboarding && !(hintsEnabled && !hintsDone && !isAdmin) && !activeModal && (
        <FortuneGate ready={!!profileOk && !!botsLoaded} suppressIntro={isTrialPending} />
      )}
    </>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  )
}
